import { VFC } from 'react';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import { useFormSecurely } from 'lib/security/reactHookForm';

const ResizableTextArea = styled(TextArea)`
  resize: vertical;
  height: 300px;
`;

interface FormEditSARNarrativeTemplateProps {
  defaultValues?: FormEditSARNarrativeTemplateData;
  allNarrativeTemplateNames: string[];
  handleUpdateNarrativeTemplate: (data: FormEditSARNarrativeTemplateData) => void;
  handleClose: () => void;
}

const FormEditSARNarrativeTemplate: VFC<FormEditSARNarrativeTemplateProps> = ({
  defaultValues,
  allNarrativeTemplateNames,
  handleUpdateNarrativeTemplate,
  handleClose,
}) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useFormSecurely<FormEditSARNarrativeTemplateData>({
    defaultValues,
    mode: 'onChange',
  });

  return (
    <form onSubmit={handleSubmit(handleUpdateNarrativeTemplate)}>
      <Label id='form-edit-sar-narrative-template__name_label' size='md' weight='bold'>
        Name
      </Label>
      <Input
        {...register('name', {
          required: 'Please select a name for your narrative template.',
          pattern: {
            value: /^[_a-zA-Z0-9][_a-zA-Z0-9\s]*$/,
            message: 'Please use letters, numbers, underscores, or spaces only.',
          },
          validate: (name: string) =>
            !allNarrativeTemplateNames.includes(name) ||
            'A template with this name already exists. Please choose a different name.',
        })}
        disabled={isSubmitting}
        role='input'
        aria-labelledby='form-edit-sar-narrative-template__name_label'
      />
      {errors['name'] && <FormError>{errors['name'].message}</FormError>}
      <Spacer height={1} />

      <Label size='md' weight='bold'>
        Body
      </Label>
      <ResizableTextArea
        {...register('body', {
          required: 'Please enter a body for your narrative template.',
        })}
        size='md'
        disabled={isSubmitting}
      />
      <Spacer height={2} />
      <HStack equal={true}>
        <Button
          id='form-edit-sar-narrative-template__cancel_button'
          variant='basic'
          disabled={isSubmitting}
          isPending={isSubmitting}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          type='submit'
          disabled={isSubmitting || !isValid}
          isPending={isSubmitting}
        >
          Save
        </Button>
      </HStack>
    </form>
  );
};

export interface FormEditSARNarrativeTemplateData {
  name: string;
  body: string;
}

export default FormEditSARNarrativeTemplate;
